import * as React from 'react';
import { useEditorState } from '@tiptap/react';

import { useTiptapEditor } from '../../hooks/use-tiptap-editor';
import { useUiEditorState } from '../../hooks/use-ui-editor-state';
import {
  useCommentAuthor,
  type CommentReply,
} from '../../../../ui/tiptap/extensions/comment-mark';
import { FloatingElement } from '../../../../ui/tiptap/tiptap-ui-utils/floating-element';
import { commentBelowFloatingOptions } from './comment-anchor';
import { CommentThreadPanel } from './comment-thread-panel';
import { useCommentThreadActions } from './comment-thread-actions';
import { useCommentThreads } from './comment-threads';
import { useCommentOutsidePress } from './comment-outside-press';
import { useCommentMarginMode } from './comment-margin-host';
import { useCommentsRail } from './comments-rail-provider';

interface ActiveComment {
  commentId: string;
  replies: CommentReply[];
  resolved: boolean;
}

/**
 * Popover that shows an existing comment thread when the caret sits inside a commented
 * range, with reply / resolve / delete actions. Read-only editors show the thread but no
 * actions. Positioning + editor wiring live here; the thread UI is {@link CommentThreadPanel}.
 *
 * Suppressed wherever this same thread is already on screen: as a card in the
 * document's margin, or opened in the comments index beside it. All three select off
 * the same caret, so leaving this mounted floats a second copy of the conversation the
 * reader is looking at.
 *
 * An index showing a *different* thread is not a conflict — that is someone reading one
 * comment in the column and clicking another in the prose — so the test is on the
 * thread. It is also on the column being open: "already on screen" is the question,
 * and a closed panel is not showing anything, whatever it last had open.
 */
export function CommentThreadPopover() {
  const { editor } = useTiptapEditor();
  const author = useCommentAuthor();
  const { commentInputVisible } = useUiEditorState(editor);
  const marginMode = useCommentMarginMode();
  const rail = useCommentsRail();
  const actions = useCommentThreadActions(editor, author);

  const { threads } = useCommentThreads(editor);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = React.useState(false);

  const active = useEditorState({
    editor,
    selector: ({ editor }): ActiveComment | null => {
      if (!editor) {
        return null;
      }
      if (!editor.state.selection.empty) {
        return null;
      }
      if (!editor.isActive('comment')) {
        return null;
      }
      const attrs = editor.getAttributes('comment');
      if (attrs['commentId'] == null) {
        return null;
      }
      return {
        commentId: attrs['commentId'] as string,
        replies: (attrs['replies'] as CommentReply[] | undefined) ?? [],
        resolved: (attrs['resolved'] as boolean | undefined) ?? false,
      };
    },
  });

  // Moving to another thread is a fresh start: a card dismissed here should not stay
  // shut when the caret lands in a different conversation.
  React.useEffect(() => {
    setDismissed(false);
  }, [active?.commentId]);

  useCommentOutsidePress({
    enabled: active != null,
    isInside: (target) => cardRef.current?.contains(target) === true,
    onOutside: () => setDismissed(true),
    onCommentPress: () => setDismissed(false),
  });

  // The summary carries the anchored words and the reply list; the editor state above
  // is what says which thread the caret is in.
  const thread =
    active == null
      ? undefined
      : threads.find((candidate) => candidate.commentId === active.commentId);

  if (
    editor === null ||
    author === null ||
    active == null ||
    thread === undefined ||
    dismissed ||
    commentInputVisible ||
    marginMode === 'cards' ||
    (rail?.open === true && rail.openThreadId === active.commentId)
  ) {
    return null;
  }

  return (
    <FloatingElement
      shouldShow={true}
      zIndex={55}
      // No `getBoundingClientRect` override: the default is the selection's own rect,
      // which is exactly what "under these words" needs. The gutter rect this used to
      // pass is a zero-width point at the editor's right edge — right for a card
      // parked in the margin, meaningless for one anchored below a line.
      floatingOptions={commentBelowFloatingOptions}
    >
      <div ref={cardRef}>
        <CommentThreadPanel
          // Remount per comment so the reply draft resets when moving between threads.
          key={active.commentId}
          thread={thread}
          author={author}
          canEdit={editor.isEditable}
          onReply={(body) =>
            actions.addReply(active.commentId, active.replies, body)
          }
          onToggleResolved={() =>
            actions.toggleResolved(
              active.commentId,
              active.replies,
              active.resolved
            )
          }
          onEditReply={(replyId, body) =>
            actions.editReply({
              commentId: active.commentId,
              replies: active.replies,
              resolved: active.resolved,
              replyId,
              body,
            })
          }
          onDeleteReply={(replyId) =>
            actions.removeReply({
              commentId: active.commentId,
              replies: active.replies,
              resolved: active.resolved,
              replyId,
            })
          }
          className="w-[26rem] max-w-[calc(100vw-2rem)] shadow-lg"
        />
      </div>
    </FloatingElement>
  );
}
